import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot
} from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { 
  FaPlus, 
  FaSearch, 
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUsers,
  FaUserTag,
  FaBoxOpen,
  FaStore,
  // --- NEW ICONS ---
  FaUserCircle,   // For View
  FaPencilAlt,    // For Edit
  FaTimesCircle   // For Delete
} from "react-icons/fa";
import "./Customers.css";

// ... (The rest of your Customers component logic stays exactly the same)
// No other changes are needed in this file except for the table body below.

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", address: "", password: "", location: "", app_type: "", hub: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const customersCollectionRef = collection(db, "customers");

  useEffect(() => {
    setLoading(true);
    const q = query(customersCollectionRef, orderBy("name"));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const customerList = querySnapshot.docs.map((doc) => ({ ...doc.data(), firestoreId: doc.id }));
      setCustomers(customerList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching customers:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...customers];

    if (searchTerm) {
      filtered = filtered.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (customer.id && customer.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
          customer.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.app_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.hub?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      let aValue = "";
      let bValue = "";
      
      switch (sortBy) {
        case "name": aValue = a.name || ""; bValue = b.name || ""; break;
        case "email": aValue = a.email || ""; bValue = b.email || ""; break;
        case "mobile": aValue = a.mobile || ""; bValue = b.mobile || ""; break;
        case "app_type": aValue = a.app_type || ""; bValue = b.app_type || ""; break;
        case "hub": aValue = a.hub || ""; bValue = b.hub || ""; break;
        case "id": aValue = a.id || ""; bValue = b.id || ""; break;
        default: aValue = a.name || ""; bValue = b.name || ""; break;
      }
      return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  }, [customers, searchTerm, sortBy, sortOrder]);
  
  const generateNewCustomerId = () => {
    let highestNumber = 0;
    customers.forEach(customer => {
      if (customer.id && customer.id.startsWith('C-')) {
        const numberPart = parseInt(customer.id.split('-')[1], 10);
        if (!isNaN(numberPart) && numberPart > highestNumber) {
          highestNumber = numberPart;
        }
      }
    });
    return `C-${String(highestNumber + 1).padStart(4, "0")}`;
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setFormData({ name: "", email: "", mobile: "", address: "", password: "", location: "", app_type: "", hub: "" });
    setCurrentCustomer(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!isEditing && !formData.password)) {
      alert("Please fill in all required fields.");
      return;
    }
  
    setLoading(true);
    if (isEditing) {
      const customerDoc = doc(db, "customers", currentCustomer.firestoreId);
      const dataToUpdate = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        address: formData.address,
        location: formData.location,
        app_type: formData.app_type,
        hub: formData.hub,
      };
      await updateDoc(customerDoc, dataToUpdate);
      alert("Customer updated successfully!");
    } else {
      const auth = getAuth();
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;
        const newDisplayId = generateNewCustomerId();
        const newCustomerData = {
          id: newDisplayId,
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          address: formData.address,
          password: formData.password,
          location: formData.location,
          app_type: formData.app_type,
          hub: formData.hub,
          role: "user",
          createdAt: new Date()
        };
        const customerDocRef = doc(db, "customers", user.uid);
        await setDoc(customerDocRef, newCustomerData);
        alert(`Customer added successfully! Display ID: ${newDisplayId}`);
      } catch (error) {
        alert(`Error: ${error.message}`);
      }
    }
    setLoading(false);
    closeModal();
  };
  
  const handleEdit = (customer) => {
    setIsEditing(true);
    setCurrentCustomer(customer);
    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      mobile: customer.mobile || "",
      address: customer.address || "",
      password: "",
      location: customer.location || "",
      app_type: customer.app_type || "",
      hub: customer.hub || "",
    });
    openModal();
  };

  const handleDelete = async (firestoreId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      const customerDoc = doc(db, "customers", firestoreId);
      await deleteDoc(customerDoc);
      alert("Customer deleted successfully!");
    }
  };

  const viewCustomerDetails = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };
  
  const handleSort = (field) => {
    const newSortOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newSortOrder);
  };

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);
  
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Customers...</p>
      </div>
    );
  }

  return (
    <div className="customers-container">
      {/* (Header, controls, and modals remain the same) */}
      <div className="customers-header">
        <h1 className="customers-title"><FaUsers className="title-icon" />Customer Management</h1>
      </div>

      <div className="controls-section">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input type="text" placeholder="Search anything..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input"/>
        </div>
        <div className="filter-controls">
          <button className="filter-toggle" onClick={() => setShowFiltersModal(true)}><FaFilter /> Sort</button>
          <button className="add-button" onClick={openModal}><FaPlus /> Add Customer</button>
        </div>
      </div>

      {showFiltersModal && (
         <div className="filters-modal-overlay" onClick={() => setShowFiltersModal(false)}>
           <div className="filters-modal" onClick={(e) => e.stopPropagation()}>
             <div className="filters-panel">
               <div className="filter-group">
                 <label>Sort by:</label>
                 <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
                   <option value="name">Customer Name</option>
                   <option value="email">Email</option>
                   <option value="mobile">Mobile Number</option>
                   <option value="app_type">App Type</option>
                   <option value="hub">Hub</option>
                   <option value="id">Customer ID</option>
                 </select>
                 <button className={`sort-order ${sortOrder}`} onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}>
                   {sortOrder === "asc" ? "↑" : "↓"}
                 </button>
               </div>
             </div>
           </div>
         </div>
      )}

      <div className="table-container">
        <table className="customers-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("name")} className="sortable"><FaUser className="th-icon" />Name</th>
              <th onClick={() => handleSort("email")} className="sortable"><FaEnvelope className="th-icon" />Email</th>
              <th onClick={() => handleSort("mobile")} className="sortable"><FaPhone className="th-icon" />Mobile</th>
              <th onClick={() => handleSort("app_type")} className="sortable"><FaBoxOpen className="th-icon" />App Type</th>
              <th onClick={() => handleSort("hub")} className="sortable"><FaStore className="th-icon" />Hub</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map((customer) => (
              <tr key={customer.firestoreId} className="table-row">
                <td data-label="Name">{customer.name}</td>
                <td data-label="Email">{customer.email}</td>
                <td data-label="Mobile">{customer.mobile || "N/A"}</td>
                <td data-label="App Type">{customer.app_type || "N/A"}</td>
                <td data-label="Hub">{customer.hub || "N/A"}</td>
                <td data-label="Actions">
                  <div className="action-buttons">
                    {/* --- THIS IS THE ONLY PART THAT CHANGED --- */}
                    <button className="action-button view-button" onClick={() => viewCustomerDetails(customer)} title="View Details"><FaUserCircle /></button>
                    <button className="action-button edit-button" onClick={() => handleEdit(customer)} title="Edit"><FaPencilAlt /></button>
                    <button className="action-button delete-button" onClick={() => handleDelete(customer.firestoreId)} title="Delete"><FaTimesCircle /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}><FaChevronLeft /></button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}><FaChevronRight /></button>
        </div>
      )}

      {isModalOpen && (
        <Modal
          closeModal={closeModal}
          handleSubmit={handleSubmit}
          formData={formData}
          handleInputChange={handleInputChange}
          isEditing={isEditing}
        />
      )}

      {showDetailsModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customer Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item"><label>Customer ID:</label><span>{selectedCustomer.id || "N/A"}</span></div>
                <div className="detail-item"><label>Name:</label><span>{selectedCustomer.name}</span></div>
                <div className="detail-item"><label>Email:</label><span>{selectedCustomer.email}</span></div>
                <div className="detail-item"><label>Mobile:</label><span>{selectedCustomer.mobile || "Not provided"}</span></div>
                <div className="detail-item"><label>App Type:</label><span>{selectedCustomer.app_type || "N/A"}</span></div>
                <div className="detail-item"><label>Hub:</label><span>{selectedCustomer.hub || "N/A"}</span></div>
                <div className="detail-item"><label>Role:</label><span style={{ textTransform: 'capitalize' }}>{selectedCustomer.role || "N/A"}</span></div>
                <div className="detail-item full-width"><label>Address:</label><span>{selectedCustomer.address || "Not provided"}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// (Modal component does not need any changes)
const Modal = ({ closeModal, handleSubmit, formData, handleInputChange, isEditing }) => {
  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? "Edit Customer" : "Add New Customer"}</h2>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label>Customer Name *</label><input className="modal-input" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter customer name" required /></div>
            <div className="form-group"><label>Email Address *</label><input className="modal-input" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter email address" required disabled={isEditing}/></div>
            <div className="form-group"><label>Mobile Number</label><input className="modal-input" type="tel" name="mobile" value={formData.mobile} onChange={handleInputChange} placeholder="Enter mobile number" /></div>
            <div className="form-group"><label>App Type</label><input className="modal-input" type="text" name="app_type" value={formData.app_type} onChange={handleInputChange} placeholder="e.g., meals, grocery" /></div>
            <div className="form-group"><label>Hub</label><input className="modal-input" type="text" name="hub" value={formData.hub} onChange={handleInputChange} placeholder="Enter hub name" /></div>
            <div className="form-group"><label>Address</label><textarea className="modal-input" name="address" value={formData.address} onChange={handleInputChange} placeholder="Enter full address" rows="3"></textarea></div>
            {!isEditing && (
              <div className="form-group">
                <label>Password *</label>
                <input className="modal-input" type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Create a password" required={!isEditing}/>
              </div>
            )}
            <div className="modal-actions">
              <button type="submit" className="modal-button submit-button">{isEditing ? "Update Customer" : "Create Customer"}</button>
              <button type="button" className="modal-button cancel-button" onClick={closeModal}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


export default Customers;